import { createHttpLink, ApolloClient } from "@apollo/client";
import { RubyLink, Options as RubyLinkOptions } from "./ruby-link";
import {
  defaultDataIdFromObject,
  InMemoryCache,
  PossibleTypesMap,
} from "@apollo/client/cache";

interface Params {
  initialState?: any;
  app: "retailer" | "web" | "admin" | "mhq";
}

interface Type {
  kind: "UNION" | "INTERFACE";
  name: string;
  possibleTypes: { name: string }[];
}

interface Schema {
  __schema: {
    types: Type[];
  };
}

const extractPossibleTypes = (result: Schema) => {
  const possibleTypes: PossibleTypesMap = {};

  result.__schema.types.forEach((supertype) => {
    if (supertype.possibleTypes) {
      possibleTypes[supertype.name] = supertype.possibleTypes.map(
        (subtype) => subtype.name
      );
    }
  });

  return possibleTypes;
};
function buildCache(params: Params) {
  let schema: Schema;

  if (params.app === "web") {
    schema = require("~web/scripts/fragment-types.json");
  } else if (params.app === "retailer") {
    schema = require("~retailer/scripts/fragment-types.json");
  } else if (params.app === "mhq") {
    schema = require("~mhq/scripts/fragment-types.json");
  } else if (params.app === "admin") {
    schema = require("~admin/scripts/fragment-types.json");
  } else {
    throw new Error("Could not find fragment matcher");
  }

  const possibleTypes: PossibleTypesMap = extractPossibleTypes(schema);

  let cache = new InMemoryCache({
    possibleTypes,
    dataIdFromObject(responseObject) {
      return defaultDataIdFromObject(responseObject);
    },
  });
  if (params != null && params.initialState != null) {
    cache = cache.restore(params.initialState);
  }

  return cache;
}

export function createRubyClient(params: RubyLinkOptions & Params) {
  const { ...linkOptions } = params;

  // Remember that this is the interface the SSR server will use to connect to
  // the API server, so we need to ensure it isn't firewalled, etc
  const link = new RubyLink(linkOptions);
  const cache = buildCache(params);

  return new ApolloClient({ ssrMode: true, link, cache });
}

// Will extract all meta[http-equiv] tags on the page and use them in the
// GraphQL requests
function extractEmbeddedHtmlHeaders() {
  const headers: { [k: string]: string } = {};

  document
    .querySelectorAll<HTMLMetaElement>("meta[http-equiv]")
    .forEach((elem) => (headers[elem.httpEquiv] = elem.content));

  return headers;
}

// A custom version of fetch which handles redirects (typically due to workspace mismatches)
const fetchWithRedirectAwareness: typeof fetch = async (url, options) => {
  const response = await fetch(url, {
    ...options,
    mode: "same-origin",
    redirect: "follow",
  });

  if (response.redirected) {
    window.location.href = response.url;
  }
  return response;
};

function extractCsrfHeaders() {
  const headers: { [k: string]: string } = {};
  const csrfTokenElement = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]'
  );
  if (csrfTokenElement) {
    headers["X-CSRF-Token"] = csrfTokenElement.content;
  }

  return headers;
}

export function createHttpClient(uri: string, params: Params) {
  const headers = {
    ...extractEmbeddedHtmlHeaders(),
    ...extractCsrfHeaders(),
    accept: "application/json",
    "content-type": "application/json",
  };

  const link = createHttpLink({
    uri,
    credentials: "same-origin",
    fetch: fetchWithRedirectAwareness,
    includeUnusedVariables: true,
    headers,
  });

  const cache = buildCache(params);

  return new ApolloClient({
    link,
    cache,
    connectToDevTools: process.env.NODE_ENV === "development",
  });
}

export function createNodeClient(
  params: Params & {
    app: string;
    fetch: typeof fetch;
    headers: Record<string, string>;
    gqlHost: string;
  }
) {
  const headers = {
    accept: "application/json",
    "content-type": "application/json",
    ...params.headers,
  };

  const link = createHttpLink({
    uri: `${params.gqlHost}/${params.app}/graphql/internal`.replace("/web", ""),
    fetch: params.fetch,
    includeUnusedVariables: true,
    headers,
  });

  const cache = buildCache(params);

  return new ApolloClient({
    ssrMode: true,
    link,
    cache,
  });
}
